import _slicedToArray from "/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { spy, observable, Reaction, computed } from 'mobx';
import { useState, useRef, useMemo, useEffect, useCallback, forwardRef, memo } from 'react';

if (!useState) {
  throw new Error("mobx-react-lite requires React 16.7 to be available");
}

if (!spy) {
  throw new Error("mobx-react-lite requires mobx 4 to be available");
}

function useObservable(initialValue) {
  return useRef(observable(initialValue)).current;
}

function useComputed(func) {
  var inputs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var computed$$1 = useMemo(function () {
    return computed(func);
  }, inputs);
  return computed$$1.get();
}
/**
 * Adds an observable effect (reaction, autorun, or anything else that returns a disposer) that will be registered upon component creation and disposed upon unmounting.
 * Returns the generated disposer for early disposal.
 *
 * @export
 * @template D
 * @param {() => D} disposerGenerator A function that returns the disposer of the wanted effect.
 * @param {ReadonlyArray<any>} [inputs=[]] If you want the effect to be automatically re-created when some variable(s) are changed then pass them in this array.
 * @returns {D}
 */


function useDisposable(disposerGenerator) {
  var inputs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var disposerRef = useRef(undefined);
  useMemo(function () {
    disposerRef.current = disposerGenerator();
  }, inputs);
  useEffect(function () {
    return function () {
      if (disposerRef.current && typeof disposerRef.current === "function") {
        disposerRef.current();
      }
    };
  }, inputs);
  return disposerRef.current;
}
/**
 * Adds an observable effect (reaction, autorun, or anything else that returns a disposer) that will be registered upon component creation and disposed upon unmounting.
 * Returns the generated disposer for early disposal.
 *
 * @deprecated Renamed to useDisposable for a more universal use
 * @export
 * @template D
 * @param {() => D} disposerGenerator A function that returns the disposer of the wanted effect.
 * @param {ReadonlyArray<any>} [inputs=[]] If you want the effect to be automatically re-created when some variable(s) are changed then pass them in this array.
 * @returns {D}
 */


function useObservableEffect(disposerGenerator) {
  var inputs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return useDisposable(disposerGenerator, inputs);
}

var globalIsUsingStaticRendering = false;

function useStaticRendering(enable) {
  globalIsUsingStaticRendering = enable;
}

function isUsingStaticRendering() {
  return globalIsUsingStaticRendering;
}

var EMPTY_ARRAY = [];

function useUnmount(fn) {
  useEffect(function () {
    return fn;
  }, EMPTY_ARRAY);
}

function useForceUpdate() {
  var _useState = useState(1),
      _useState2 = _slicedToArray(_useState, 2),
      tick = _useState2[0],
      setTick = _useState2[1];

  var update = useCallback(function () {
    setTick(tick + 1);
  }, []);
  return update;
}

function useObserver(fn) {
  var baseComponentName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "observed";

  if (isUsingStaticRendering()) {
    return fn();
  } // forceUpdate 2.0


  var forceUpdate = useForceUpdate();
  var reaction = useRef(new Reaction("observer(".concat(baseComponentName, ")"), function () {
    forceUpdate();
  }));
  useUnmount(function () {
    reaction.current.dispose();
  }); // render the original component, but have the
  // reaction track the observables, so that rendering
  // can be invalidated (see above) once a dependency changes

  var rendering;
  reaction.current.track(function () {
    rendering = fn();
  });
  return rendering;
} // n.b. base case is not used for actual typings or exported in the typing files


function observer(baseComponent, options) {
  // The working of observer is explaind step by step in this talk: https://www.youtube.com/watch?v=cPF4iBedoF0&feature=youtu.be&t=1307
  if (isUsingStaticRendering()) {
    return baseComponent;
  }

  var realOptions = Object.assign({
    forwardRef: false
  }, options);
  var baseComponentName = baseComponent.displayName || baseComponent.name;

  var wrappedComponent = function wrappedComponent(props, ref) {
    return useObserver(function () {
      return baseComponent(props, ref);
    }, baseComponentName);
  }; // memo; we are not intested in deep updates
  // in props; we assume that if deep objects are changed,
  // this is in observables, which would have been tracked anyway


  var memoComponent;

  if (realOptions.forwardRef) {
    // we have to use forwardRef here because:
    // 1. it cannot go before memo, only after it
    // 2. forwardRef converts the function into an actual component, so we can't let the baseComponent do it
    //    since it wouldn't be a callable function anymore
    memoComponent = memo(forwardRef(wrappedComponent));
  } else {
    memoComponent = memo(wrappedComponent);
  }

  memoComponent.displayName = baseComponentName;
  return memoComponent;
}

function ObserverComponent(_ref) {
  var children = _ref.children,
      render = _ref.render;
  var component = children || render;

  if (typeof component !== "function") {
    return null;
  }

  return useObserver(component);
}

ObserverComponent.propTypes = {
  children: ObserverPropsCheck,
  render: ObserverPropsCheck
};
ObserverComponent.displayName = "Observer";

function ObserverPropsCheck(props, key, componentName, location, propFullName) {
  var extraKey = key === "children" ? "render" : "children";
  var hasProp = typeof props[key] === "function";
  var hasExtraProp = typeof props[extraKey] === "function";

  if (hasProp && hasExtraProp) {
    return new Error("MobX Observer: Do not use children and render in the same time in`" + componentName);
  }

  if (hasProp || hasExtraProp) {
    return null;
  }

  return new Error("Invalid prop `" + propFullName + "` of type `" + typeof props[key] + "` supplied to" + " `" + componentName + "`, expected `function`.");
}

export { useObservable, useComputed, useDisposable, useObservableEffect, isUsingStaticRendering, useStaticRendering, observer, useObserver, ObserverComponent as Observer };